import { useTabQueryParam } from "@/hooks/use-tab-query-param";
import AppLayout from "@/components/app-layout";
import { BetaFeatureRoute } from "@/components/beta-feature-route";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { usePageTitle } from "@/hooks/use-page-title";
import { PropertiesTab } from "./app-collections/PropertiesTab";
import { SettingsTab } from "./app-collections/SettingsTab";
import { ActivityLogTab } from "./app-collections/ActivityLogTab";

// Collections (Task #4830). The old "Termination & Payments" tab held nothing
// but a single "Process Collections" button, which made a feature that was
// actually running on a daily cron look manual. It is replaced by Properties —
// the per-property enable/disable surface — with "Run now" living there.
// "termination" stays in the allow-list so old bookmarks and the guide tour
// land on Properties instead of a blank panel.
const TABS = ["properties", "settings", "activity", "termination"] as const;

export default function AppCollections() {
  return <BetaFeatureRoute featureKey="collections"><CollectionsPage /></BetaFeatureRoute>;
}

function CollectionsPage() {
  usePageTitle("Collections");
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("properties");
  const [rawTab, setActiveTab] = useTabQueryParam("tab", "properties", { tabs: [...TABS] });
  const activeTab = rawTab === "termination" ? "properties" : rawTab;

  const tabButton = (key: "properties" | "settings" | "activity", label: string, testId: string, tourId?: string) => (
    <button
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        activeTab === key
          ? "border-brand text-brand"
          : "border-transparent text-gray-500 hover:text-gray-700"
      }`}
      onClick={() => setActiveTab(key)}
      data-testid={testId}
      {...(tourId ? { "data-tour": tourId } : {})}
    >
      {label}
    </button>
  );

  return (
    <AppLayout>
      <div className="p-6">
        <ViewOnlyBanner area="properties" />
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="text-collections-title" data-tour="collections-heading">
              Collections
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Automated payment-arrangement messaging, enabled per property.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="collections" />
            <GuideButton pageKey="collections" />
          </div>
        </div>
        <GuideNudgeBanner pageKey="collections" />

        <div className="mb-6 flex border-b border-gray-200">
          {tabButton("properties", "Properties", "tab-properties")}
          {tabButton("settings", "Settings", "tab-settings")}
          {tabButton("activity", "Activity Log", "tab-activity")}
        </div>

        {activeTab === "properties" && (
          <PropertiesTab readOnly={readOnly} onGoToSettings={() => setActiveTab("settings")} />
        )}
        {activeTab === "settings" && <SettingsTab readOnly={readOnly} />}
        {activeTab === "activity" && <ActivityLogTab />}
      </div>
    </AppLayout>
  );
}
